/**
 * AGROMX Frontend - Roles List View
 * Vista de administración de roles del sistema
 */

import roleService from '../services/role.service.js';
import modal from '../utils/modal.js';
import permisos from '../services/permissions.service.js';

export default {
  data: {
    roles: [],
    loading: true,
    editingRole: null
  },

  async render() {
    return `
      <div>
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Roles y Permisos</h2>
            ${permisos.canCreate('roles') ? `
              <button class="btn btn-primary" onclick="window.rolesView.abrirModal()">
                + Nuevo Rol
              </button>
            ` : ''}
          </div>

          <!-- Tabla -->
          <div id="rolesTable">
            <div style="text-align: center; padding: var(--spacing-xl);">
              <div class="loading-spinner" style="margin: 0 auto;"></div>
              <p>Cargando roles...</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Crear/Editar Rol -->
      <div id="roleModal" class="modal" style="display: none;">
        <div class="modal-content modal-sm">
          <div class="modal-header">
            <h2 id="modalTitle">Nuevo Rol</h2>
            <button class="modal-close" id="btnCerrarModal">&times;</button>
          </div>
          <form id="roleForm">
            <div class="modal-body">
              <div class="form-group">
                <label class="form-label required">Nombre</label>
                <input
                  type="text"
                  id="inputNombre"
                  class="form-input"
                  placeholder="Ej. Auditor Externo"
                  minlength="3"
                  maxlength="100"
                  required
                />
              </div>
              <div class="form-group">
                <label class="form-label">Descripción</label>
                <textarea
                  id="inputDescripcion"
                  class="form-input"
                  rows="3"
                  maxlength="255"
                  placeholder="Describe brevemente qué puede hacer este rol (opcional)"
                ></textarea>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" id="btnCancelar">Cancelar</button>
              <button type="submit" class="btn btn-primary" id="btnGuardar">Guardar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  },

  async afterRender() {
    if (!permisos.canView('roles')) {
      if (permisos.primeraRuta) { router.navigate(permisos.primeraRuta); }
      else { document.getElementById('content').innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:60vh;"><div style="text-align:center;color:var(--primary);"><h2>Acceso denegado</h2><p>No tienes permisos para acceder a este módulo.</p></div></div>'; }
      return;
    }
    window.rolesView = this;
    await this.cargarRoles();
    this.setupEventListeners();
  },

  setupEventListeners() {
    document.getElementById('btnCerrarModal')?.addEventListener('click', () => {
      this.cerrarModal();
    });
    document.getElementById('btnCancelar')?.addEventListener('click', () => {
      this.cerrarModal();
    });
    document.getElementById('roleForm')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.guardarRol();
    });
    document.getElementById('roleModal')?.addEventListener('click', (e) => {
      if (e.target.id === 'roleModal') this.cerrarModal();
    });
  },

  async cargarRoles() {
    const container = document.getElementById('rolesTable');
    this.data.loading = true;
    try {
      const response = await roleService.list();
      this.data.roles = response.roles || [];
      this.renderTabla();
    } catch (err) {
      console.error('Error cargando roles:', err);
      container.innerHTML = `
        <div style="text-align: center; padding: var(--spacing-xl); color: var(--danger);">
          <p>Error al cargar roles: ${err.message}</p>
        </div>
      `;
    } finally {
      this.data.loading = false;
    }
  },

  renderTabla() {
    const container = document.getElementById('rolesTable');
    if (!container) return;

    if (!this.data.roles.length) {
      container.innerHTML = `
        <div style="text-align: center; padding: var(--spacing-xl); color: var(--gray-500);">
          <p>No hay roles registrados</p>
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <table class="table">
        <thead>
          <tr>
            <th></th>
            <th>#</th>
            <th>Nombre</th>
            <th class="tbl-hide-mobile">Estado</th>
            <th class="tbl-hide-mobile">Acciones</th>
          </tr>
        </thead>
        <tbody>
          ${this.data.roles.map((rol, index) => {
            const badgeEstado = rol.activo
              ? '<span class="badge badge-success">Activo</span>'
              : '<span class="badge badge-warning">Inactivo</span>';
            const badgeSistema = '';

            const disabledSistema = rol.es_sistema ? 'disabled' : '';
            const disabledEliminar = (rol.es_sistema || rol.usuarios_activos > 0) ? 'disabled' : '';

            const btnPermisos = permisos.can('roles', 'asignar_permisos') ? `<button class="btn btn-sm btn-primary" onclick="window.rolesView.verPermisos(${rol.id})" ${disabledSistema}>Permisos</button>` : '';
            const btnEditar = permisos.canEdit('roles') ? `<button class="btn btn-sm btn-secondary" onclick="window.rolesView.editarRol(${rol.id})" ${disabledSistema}>Editar</button>` : '';
            let btnDesactivar;
            if (!permisos.can('roles', 'cambiar_estado')) {
              btnDesactivar = '';
            } else if (rol.es_sistema) {
              btnDesactivar = `<button class="btn btn-sm btn-danger" onclick="window.rolesView.alertNoDesactivar('Es un rol de sistema y no se puede desactivar.')">${rol.activo ? 'Desactivar' : 'Activar'}</button>`;
            } else if (rol.activo && rol.usuarios_activos > 0) {
              btnDesactivar = `<button class="btn btn-sm btn-danger" onclick="window.rolesView.alertNoDesactivar('Este rol tiene ${rol.usuarios_activos} usuario(s) activo(s). Reasígnalos antes de desactivar.')">Desactivar</button>`;
            } else {
              const labelToggle = rol.activo ? 'Desactivar' : 'Activar';
              const classToggle = rol.activo ? 'btn-danger' : 'btn-success';
              btnDesactivar = `<button class="btn btn-sm ${classToggle}" onclick="window.rolesView.toggleActivoRol(${rol.id})">${labelToggle}</button>`;
            }

            return `
              <tr>
                <td>
                  <button class="tbl-expand-btn" onclick="window.rolesView.toggleRow(${rol.id})">&#9654;</button>
                </td>
                <td>${index + 1}</td>
                <td><strong>${this.escapeHtml(rol.nombre)}</strong>${badgeSistema}</td>
                <td class="tbl-hide-mobile">${badgeEstado}</td>
                <td class="tbl-hide-mobile">
                  ${btnPermisos}
                  ${btnEditar}
                  ${btnDesactivar}
                </td>
              </tr>
              <tr class="tbl-detail-row" id="detail-${rol.id}">
                <td colspan="5" class="tbl-detail-cell">
                  <dl class="tbl-detail-grid">
                    <dt>Estado</dt><dd>${badgeEstado}</dd>
                    ${rol.descripcion ? `<dt>Descripción</dt><dd>${this.escapeHtml(rol.descripcion)}</dd>` : ''}
                  </dl>
                  <div style="margin-top: var(--spacing-sm); display: flex; gap: var(--spacing-xs);">
                    ${btnPermisos}
                    ${btnEditar}
                    ${btnDesactivar}
                  </div>
                </td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    `;
  },

  toggleRow(id) {
    const detailRow = document.getElementById(`detail-${id}`);
    const btn = event?.currentTarget;
    if (detailRow) {
      detailRow.classList.toggle('open');
      btn?.classList.toggle('open');
    }
  },

  abrirModal(rol = null) {
    this.data.editingRole = rol;
    document.getElementById('modalTitle').textContent = rol ? 'Editar Rol' : 'Nuevo Rol';
    const inputNombre = document.getElementById('inputNombre');
    inputNombre.value = rol?.nombre || '';
    inputNombre.readOnly = !!rol;
    inputNombre.style.opacity = rol ? '0.6' : '1';
    document.getElementById('inputDescripcion').value = rol?.descripcion || '';
    document.getElementById('roleModal').style.display = 'flex';
  },

  cerrarModal() {
    document.getElementById('roleModal').style.display = 'none';
    this.data.editingRole = null;
  },

  async editarRol(id) {
    const rol = this.data.roles.find(r => r.id === id);
    if (rol) this.abrirModal(rol);
  },

  async guardarRol() {
    const nombre = document.getElementById('inputNombre').value.trim();
    const descripcion = document.getElementById('inputDescripcion').value.trim();
    if (nombre.length < 3) {
      alert('El nombre debe tener al menos 3 caracteres');
      return;
    }

    const data = this.data.editingRole
      ? { descripcion: descripcion || null }
      : { nombre, descripcion: descripcion || null };

    try {
      if (this.data.editingRole) {
        await roleService.update(this.data.editingRole.id, data);
      } else {
        await roleService.create(data);
      }
      this.cerrarModal();
      await this.cargarRoles();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err.message || 'Error al guardar';
      alert(`Error: ${msg}`);
    }
  },

  alertNoDesactivar(mensaje) {
    modal.alert('No se puede desactivar', mensaje);
  },

  async toggleActivoRol(id) {
    const rol = this.data.roles.find(r => r.id === id);
    if (!rol) return;

    const accion = rol.activo ? 'desactivar' : 'activar';
    modal.confirm(
      'Cambiar estado del rol',
      `¿Está seguro de ${accion} el rol <strong>${this.escapeHtml(rol.nombre)}</strong>?`,
      async () => {
        try {
          await roleService.update(id, { activo: !rol.activo });
          await this.cargarRoles();
        } catch (err) {
          const msg = err?.response?.data?.error?.message || err.message || `Error al ${accion}`;
          modal.alert('Error', msg);
        }
      }
    );
  },

  verPermisos(id) {
    window.location.href = `/admin/roles/${id}/permisos`;
  },

  escapeHtml(str) {
    if (str === null || str === undefined) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
};
